/**
 * 1 プレイあたりの動的プログラム起動（クローン等への startObjectPrograms）の
 * 上限。onPlay → control_clone（自分自身を複製）のような自己複製プログラムが
 * 際限なくハンドラ・オブジェクトを増やしてフリーズするのを防ぐ。
 */
export const MAX_DYNAMIC_PROGRAM_STARTS = 200

/**
 * 1 プレイあたりに control_clone で生成できるクローン総数の上限。forever/repeat
 * 内のクローン生成（addObject）が際限なく World を膨らませ、World 購読コールバックや
 * touchWatcher の O(N^2) 処理でページが無反応化するのを防ぐ安全網。
 */
export const MAX_DYNAMIC_CLONES = 500

/**
 * RunSession.wait 共有クロックの tick 間隔（ms）。実機 Heartbeat の 30Hz
 * スロットルに合わせる（LUA_WAIT_SECONDS と同じ周期）。
 */
const CLOCK_TICK_MS = 1000 / 30

/**
 * 判定の許容誤差（ms）。setInterval の実際の発火間隔は整数 ms に丸められる
 * のに対し、待機の目標時刻（例: LUA_WAIT_SECONDS=1/30 秒 → 33.333…ms）は
 * 端数を持つ。丸めなしで比較すると、1 tick では目標にわずかに届かず、
 * 常に 1 tick 分（約 33ms）よけいに待ってしまい、frame ごとに wait する
 * ループ（例: 「ずっと」内の毎周回 wait）の進みが実質半分の速度になる。
 * ここでは丸め誤差ぶんだけを吸収したいので、tick 間隔そのものではなく
 * setInterval の整数 ms 丸めで生じ得る最大誤差（1ms 未満）に、実行環境の
 * ジッタぶんの余裕を加えた程度の小さな値にとどめる（tick 間隔の半分等の
 * 大きな許容誤差にすると、短い wait 秒数（例: 0.05 秒）が実際の経過時間より
 * 早く解決してしまう）。
 */
const CLOCK_READY_EPSILON_MS = 2

/** RunSession.wait の内部待機エントリ。 */
interface PendingWait {
  /** 共有クロックの目標経過時刻（ms）。 */
  readonly targetMs: number
  readonly resolve: () => void
}

/**
 * 1 回のプレイ実行（start〜stop）のスコープ。
 *
 * - 全体の中断（stop）と、オブジェクト単位の中断（control_destroy）を分けて扱う。
 * - オブジェクトごとに、全体 signal に連動する AbortController を持つ。
 *   全体を abort すると各オブジェクト signal も abort される（destroy で個別 abort 可）。
 */
export class RunSession {
  private readonly rootController = new AbortController()
  private readonly objectControllers = new Map<string, AbortController>()
  /** このプレイで消費した動的プログラム起動数（MAX_DYNAMIC_PROGRAM_STARTS まで）。 */
  private dynamicProgramStarts = 0
  /** このプレイで生成した動的クローン数（MAX_DYNAMIC_CLONES まで）。 */
  private dynamicClones = 0
  /**
   * プレイ全体で共有する待機クロックの経過時刻（ms）。tick ごとに、前回の
   * tick からの実経過時間（performance.now() の差分）を足し込む。固定
   * CLOCK_TICK_MS を足すだけだと、タブのバックグラウンド化やメインスレッド
   * 詰まりで setInterval のコールバックが遅延・間引きされた場合に実時間より
   * 進みが遅くなり、待機がどんどん遅延してしまう。System の壁時計を進退
   * させる Date.now() ではなく単調増加する performance.now() を使うのは、
   * ユーザー操作等で壁時計が補正されても待機の進み方が影響を受けないため
   * （vitest の vi.useFakeTimers() は既定で performance も差し替えるため
   * advanceTimersByTimeAsync でのテストとも問題なく噛み合う）。
   * white（Blockly インタプリタ）・green（環境ビヘイビア）双方の wait を
   * この単一クロックへ揃えることで、個別 setTimeout チェーンの積み重ね誤差
   * により両者が相対的にずれていく問題（Roblox Studio では単一 Heartbeat を
   * 共有するため起きない）を防ぐ。
   */
  private elapsedMs = 0
  /** 直近 tick の performance.now()。実経過時間の差分計算に使う。 */
  private lastTickAtMs = performance.now()
  /** 次の tick で判定待ちの wait 一覧。 */
  private pendingWaits: PendingWait[] = []
  /**
   * 共有クロックの tick タイマー。プレイ開始から stop() までセッション
   * 生存期間ぶん動かし続ける（wait の有無で作り直さない）。pendingWaits の
   * 出入りに合わせて setInterval を都度張り直すと、vi.useFakeTimers() の
   * advanceTimersByTimeAsync がタイマー破棄・再登録のタイミングを跨いで
   * 処理を打ち切ってしまい、待機が進まなくなることがあったため。
   */
  private readonly intervalId: ReturnType<typeof setInterval>

  constructor(objectIds: ReadonlyArray<string>) {
    for (const objectId of objectIds) {
      const controller = new AbortController()
      this.objectControllers.set(objectId, controller)
    }
    // 全体 abort 時に各オブジェクトも abort する。
    this.rootController.signal.addEventListener(
      'abort',
      () => {
        for (const controller of this.objectControllers.values()) {
          controller.abort()
        }
      },
      { once: true },
    )
    this.intervalId = setInterval(() => this.tick(), CLOCK_TICK_MS)
  }

  /** プレイ全体の signal。stop() で abort される。ミッションビヘイビア用。 */
  get rootSignal(): AbortSignal {
    return this.rootController.signal
  }

  /**
   * 実行中に生成されたオブジェクト（クローン等）の controller を登録する。
   * 全体 abort のリスナーは abort 時点の objectControllers を走査するため、
   * 後から登録した controller も stop() で確実に中断される。
   */
  registerObject(objectId: string): void {
    if (this.objectControllers.has(objectId)) {
      return
    }
    const controller = new AbortController()
    this.objectControllers.set(objectId, controller)
    if (this.rootController.signal.aborted) {
      controller.abort()
    }
  }

  /** 指定オブジェクトのハンドラ実行用 signal。未知 ID は全体 signal にフォールバック。 */
  signalFor(objectId: string): AbortSignal {
    return (
      this.objectControllers.get(objectId)?.signal ?? this.rootController.signal
    )
  }

  /** オブジェクト単位の中断（control_destroy）。 */
  abortObject(objectId: string): void {
    this.objectControllers.get(objectId)?.abort()
  }

  /** プレイ全体の中断（stop）。 */
  abortAll(): void {
    this.rootController.abort()
    clearInterval(this.intervalId)
    // 残っている待機（BehaviorContext.wait は rootSignal 直結で abort
    // リスナー経由で解決するが、念のため取りこぼしを解決しておく）。
    const pending = this.pendingWaits
    this.pendingWaits = []
    for (const entry of pending) {
      entry.resolve()
    }
  }

  /**
   * プレイ全体で共有する単一クロックに基づく中断可能な待機。
   * 指定秒数後か signal の abort で解決する。white/green のどちらから
   * 呼ばれても同じ setInterval ティックで判定されるため、個別 setTimeout
   * チェーンのように両者が相対的にドリフトすることがない。
   */
  wait(seconds: number, signal: AbortSignal): Promise<void> {
    return new Promise<void>((resolve) => {
      if (signal.aborted || this.rootController.signal.aborted) {
        resolve()
        return
      }
      const onAbort = (): void => {
        this.pendingWaits = this.pendingWaits.filter((p) => p !== entry)
        resolve()
      }
      const entry: PendingWait = {
        targetMs: this.currentElapsedMs() + Math.max(0, seconds * 1000),
        resolve: () => {
          // tick 側の正常解決でも abort リスナーを外す（{ once: true } は
          // abort イベントが実際に発火した場合しか自動で外れないため、
          // 外さないままだとプレイ中の大量の wait 呼び出し分だけ
          // クロージャが溜まり続けてしまう）。
          signal.removeEventListener('abort', onAbort)
          resolve()
        },
      }
      this.pendingWaits.push(entry)
      signal.addEventListener('abort', onAbort, { once: true })
    })
  }

  /**
   * 現在の共有クロック経過時刻（ms）。elapsedMs は直近の tick 時点までしか
   * 反映していないため、tick と tick の間（イベント発火等の任意タイミング）
   * で wait を登録すると、その間の経過ぶんが欠落して目標時刻が早まって
   * しまう（結果的に wait が要求より早く解決する）。tick 未反映ぶんの
   * 経過（performance.now() との差分）を都度足して現在時刻を出す。
   */
  private currentElapsedMs(): number {
    return this.elapsedMs + (performance.now() - this.lastTickAtMs)
  }

  /**
   * 共有クロックの tick。setInterval を使うのは、vitest の
   * vi.useFakeTimers()（advanceTimersByTimeAsync）がテストで待機を進める
   * 対象であり、requestAnimationFrame は対象外のため。経過時間は
   * performance.now() の実差分を積算する（固定 CLOCK_TICK_MS を足すだけだと、
   * タブのバックグラウンド化等でコールバックが間引かれた場合に実時間より
   * 進みが遅れ、待機がどんどん遅延してしまう）。
   */
  private tick(): void {
    const nowAtMs = performance.now()
    this.elapsedMs += nowAtMs - this.lastTickAtMs
    this.lastTickAtMs = nowAtMs
    const nowMs = this.elapsedMs + CLOCK_READY_EPSILON_MS
    const ready = this.pendingWaits.filter((p) => p.targetMs <= nowMs)
    if (ready.length === 0) {
      return
    }
    this.pendingWaits = this.pendingWaits.filter((p) => p.targetMs > nowMs)
    for (const entry of ready) {
      entry.resolve()
    }
  }

  /**
   * 動的プログラム起動の予算を 1 消費する。上限到達済みなら false
   * （呼び出し側は起動を打ち切る）。
   */
  tryConsumeDynamicProgramStart(): boolean {
    if (this.dynamicProgramStarts >= MAX_DYNAMIC_PROGRAM_STARTS) {
      return false
    }
    this.dynamicProgramStarts += 1
    return true
  }

  /**
   * クローン生成の予算を 1 消費する。上限到達済みなら false
   * （呼び出し側はクローン生成を打ち切る）。
   */
  tryConsumeDynamicClone(): boolean {
    if (this.dynamicClones >= MAX_DYNAMIC_CLONES) {
      return false
    }
    this.dynamicClones += 1
    return true
  }
}
